import React, { useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { useBuilderReferrerOptions } from '~/acquisition/utils/useBuilderReferrerOptions';
import CheckCircleIcon2 from '~/icons/components/CheckCircleIcon2';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import {
  SelectorDropdownButton,
  QuizDropdown,
  QuizDropdownOption,
  QuizDropdownList,
} from '../../components/QuizFormComponents';
import { Chevron } from '../QuizFormComponents/QuizDropdown';

const ParentOption = styled.div`
  padding: 16px 8px 16px 12px;

  &:hover {
    background: ${({ theme }) => theme.colors.tieredListDropdownParentOptionBg};
  }
`;

const OptionWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const SelectedOptionIndicator = styled(CheckCircleIcon2)`
  width: 16px;
  height: 16px;
  margin-right: 8px;
`;

const ParentLabel = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChildOptions = styled.div`
  padding-left: 1rem;
`;

const TieredSelectorDropdownButton = styled(SelectorDropdownButton)``;

const TieredQuizDropdown = styled(QuizDropdown)`
  max-height: 48px;
  min-height: unset;
`;

const TieredQuizDropdownContent = styled.div`
  height: 100%;
  ${({ theme }) => theme.quiz.TieredQuizDropdownContentWrapper}

  #tiered-quiz-dropdown-items {
    border-top: 1px solid ${({ theme }) => theme.colors.quizDropdownBorder};
  }
`;

const NestedChevron = styled(Chevron)`
  padding-top: 5px;
  font-size: 20px;
  vertical-align: middle;
  transform: rotate(${({ $direction }) => ($direction === 'up' ? 180 : 0)}deg);
  transform-origin: 50% 50%;
  color: ${({ theme }) => theme.colors.palette.lightBlack};
  color: ${({ theme }) => theme.colors.sublabelBlack};
`;

export default function TieredListDropdown({
  values,
  errors,
  handleChange,
  referrerButtonStyles,
  isDropdownOpen = false,
  setIsDropdownOpen,
  hasError,
}) {
  const { options, isLoading } = useBuilderReferrerOptions();
  const [expandedCodes, setExpandedCodes] = useState([]);

  const toggleExpand = (globalCode) => {
    setExpandedCodes((prevState) =>
      prevState.includes(globalCode)
        ? prevState.filter((code) => code !== globalCode)
        : [...prevState, globalCode]
    );
  };

  const renderOptions = (optionList) =>
    optionList.map((option) => {
      const hasChildren = option.children?.length > 0;
      const isExpanded = expandedCodes.includes(option.globalCode);

      if (option.referrerId && option.label && !hasChildren) {
        // Leaf Option
        return (
          <QuizDropdownOption
            value={option.referrerId.toString()}
            key={option.referrerId.toString()}
            data-selected={option.referrerId === values.referrer?.value}
            onClick={() => {
              handleChange({
                value: option.referrerId,
                label: option.label,
              });
            }}
          >
            <OptionWrapper>
              {option.referrerId === values.referrer?.value && (
                <SelectedOptionIndicator />
              )}
              {option.label}
            </OptionWrapper>
          </QuizDropdownOption>
        );
      }

      // Reject any parent options that don't have children
      if (!hasChildren) {
        return null;
      }

      // Parent Option with children
      return (
        <div key={option.globalCode || option.referrerId}>
          <ParentOption>
            <ParentLabel onClick={() => toggleExpand(option.globalCode)}>
              {option.label}
              <NestedChevron $direction={isExpanded ? 'up' : 'down'} />
            </ParentLabel>
          </ParentOption>
          {hasChildren && isExpanded && (
            <ChildOptions>{renderOptions(option.children)}</ChildOptions>
          )}
        </div>
      );
    });

  if (!options || options.length === 0) {
    if (!isLoading) {
      // eslint-disable-next-line no-console
      console.error(
        'Something went wrong. No HDYH options returned from server'
      );
    }
    return null;
  }

  return (
    <TieredQuizDropdown
      value={values.referrer?.label || ''}
      options={options}
      data-autotag="quiz_how_you_hear_about"
      $isSelected={values.referrer?.value}
      setIsDropdownOpen={setIsDropdownOpen}
    >
      <TieredQuizDropdownContent $isDropdownOpen={isDropdownOpen}>
        <TieredSelectorDropdownButton
          id="tiered-quiz-selector-dropdown"
          currentSelection={{
            value: values.referrer?.value || '',
            label: values.referrer?.label || '',
          }}
          hasError={hasError}
          errors={errors}
          dataAutotag="quiz-referrer"
          dropDownMessage={
            <FormattedMessage
              id="quiz.sizing_screen_HDYH_header"
              defaultMessage="How did you hear about us?*"
            />
          }
          buttonStyles={referrerButtonStyles}
          selectorId="quiz-selector-referrer"
        />
        <QuizDropdownList id="tiered-quiz-dropdown-items">
          {renderOptions(options)}
        </QuizDropdownList>
      </TieredQuizDropdownContent>
    </TieredQuizDropdown>
  );
}

TieredListDropdown.propTypes = {
  errors: PropTypes.object,
  handleChange: PropTypes.func.isRequired,
  hasError: PropTypes.func.isRequired,
  isDropdownOpen: PropTypes.bool,
  referrerButtonStyles: PropTypes.object,
  setIsDropdownOpen: PropTypes.func,
  values: PropTypes.object.isRequired,
};
